import { useEffect, useState } from 'react';
import type { OrderDetail } from '@/api/types';
import { useSession } from '@/stores/session';
import { legacyHref } from '@/shell/nav';
import { orderStatus } from '@/lib/status';
import { useResource, type OrderConfirmKind } from './api';
import { getOrderMoreState, OrderMoreSheet } from './OrderMoreSheet';
import { OrderConfirmDialog } from './OrderConfirmDialog';
import { OrderInvoiceDialog } from './OrderInvoiceDialog';
import { OrderChangeRequestDialog } from './OrderChangeRequestDialog';
import s from './OrderMoreSheet.module.css';

/** 从 iPad 台账行的长按/右键菜单打开，确认期间保留底层列表和筛选。 */
export function OrderLedgerActions({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { data: order, loading, error, reload } = useResource<OrderDetail>(`/api/v2/orders/${encodeURIComponent(id)}`);
  const can = useSession((state) => state.can);
  const user = useSession((state) => state.user);
  const [kind, setKind] = useState<OrderConfirmKind | 'invoice' | 'change' | null>(null);
  useEffect(() => {
    if (!loading && !error) return;
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [loading, error, onClose]);

  if (loading || error || !order) return <div className={s.overlay}>
    <button type="button" className={s.scrim} aria-label="关闭订单操作" onClick={onClose} />
    <section className={s.sheet} role="dialog" aria-modal="true" aria-label="订单操作">
      <div className={s.heading} role={error ? 'alert' : 'status'}>{error ? error.message : '正在读取订单…'}</div>
      {error && <button type="button" className={s.action} onClick={reload}>重试</button>}
      <button type="button" className={s.action} onClick={onClose}>关闭</button>
    </section>
  </div>;

  const state = getOrderMoreState(order, can, user);
  const allowed = { cancel: state.canCancel, close: state.canClose, refund: state.canRefund, delete: state.canDelete };
  const canManageInvoice = can('orders.invoice.manage') && user?.role === 'ADMIN' && ['COMPLETED', 'CLOSED'].includes(order.status);
  const changed = () => { reload(); onChanged(); };
  if (kind === 'invoice') return <OrderInvoiceDialog order={order} canManage={canManageInvoice} onClose={onClose} onSuccess={changed} />;
  if (kind === 'change') return <OrderChangeRequestDialog order={order} allowed={state.canRequestChange} onClose={onClose} onSuccess={changed} />;
  if (kind) return <OrderConfirmDialog order={order} kind={kind} allowed={allowed[kind]} onClose={onClose} onSuccess={onChanged} />;
  return <OrderMoreSheet order={order} statusLabel={orderStatus[order.status].label} can={can} user={user}
    canManageInvoice={canManageInvoice}
    onOpenInvoice={() => setKind('invoice')} onRequestChange={() => setKind('change')} onConfirm={setKind} legacyUrl={legacyHref(`/orders/${id}`)} onClose={onClose} />;
}
